/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, Typography, Button } from '@mui/material';
import React from 'react';
import NotificationList from './NotificationList';
import type { EventCategoryParams, SWEvent } from '../../types';
import { useSpaceweatherTranslation } from '../../utils/i18n';
import {
  useEventList,
  useSetAcknowledged,
} from '../../utils/spaceweather-api/hooks';
import LifeCycleDialog from '../LifeCycleDialog/LifeCycleDialog';
import { useNotificationTriggerContext } from '../NotificationTrigger';

const constructParams = (tabValue: string): EventCategoryParams => {
  if (tabValue === 'ALL') {
    return {};
  }
  return tabValue === 'KNMI'
    ? { originator: tabValue }
    : { category: tabValue };
};

/**
 * Notifications
 * View incoming notifications and issue new notifications following the notification lifecycle
 *
 * @example
 * ``` <Notifications /> ```
 */
const Notifications: React.FC = () => {
  const { t } = useSpaceweatherTranslation();

  const { notificationTriggers: newNotifications } =
    useNotificationTriggerContext();

  const { mutateAsync: setAcknowledged } = useSetAcknowledged();

  const [dialogEvent, setDialogEvent] = React.useState<SWEvent | null>(null);
  const [lifeCycleDialogMode, setLifeCycleDialogMode] = React.useState('new');
  const [lifeCycleDialogOpen, setLifeCycleDialogOpen] = React.useState(false);

  const [tabValue, setTabValue] = React.useState<string>('ALL');

  const {
    data: eventList,
    isFetching: isLoadingList,
    error: errorList,
    refetch: refetchEventList,
  } = useEventList(constructParams(tabValue));

  React.useEffect(() => {
    void refetchEventList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newNotifications]);

  const toggleDialogOpen = (): void => {
    // Refresh list on closing dialog
    if (lifeCycleDialogOpen) {
      void refetchEventList();
    }
    setLifeCycleDialogOpen(!lifeCycleDialogOpen);
  };

  React.useEffect(() => {
    if (lifeCycleDialogMode !== 'new') {
      toggleDialogOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifeCycleDialogMode]);

  const toggleCreateNotification = (): void => {
    setLifeCycleDialogMode('new');
    toggleDialogOpen();
  };

  const handleNotificatioNRowClick = async (event: SWEvent): Promise<void> => {
    setDialogEvent(event);
    if (lifeCycleDialogMode !== event.eventid) {
      setLifeCycleDialogMode(event.eventid);
      if (event.notacknowledged === true) {
        await setAcknowledged({
          data: event.eventid,
        });
      }
    } else {
      toggleDialogOpen();
    }
  };

  return (
    <Grid container spacing={1} style={{ height: '100%' }}>
      <Grid size={12}>
        <Typography
          sx={{ textAlign: 'center', fontWeight: 500 }}
          variant="subtitle1"
        >
          {t('notification-title')}
        </Typography>
      </Grid>
      <Grid size={12}>
        <NotificationList
          error={errorList}
          eventList={eventList || null}
          handleNotificatioNRowClick={handleNotificatioNRowClick}
          isLoading={isLoadingList}
          newNotifications={newNotifications!}
          onChangeTabValue={setTabValue}
          tabValue={tabValue}
        />
      </Grid>
      <Grid size={12}>
        <Grid container justifyContent="flex-end">
          <Grid sx={{ marginTop: '8px', marginRight: 1 }}>
            <Button
              color="secondary"
              data-testid="notifications-newnotification"
              onClick={toggleCreateNotification}
              size="small"
              variant="outlined"
            >
              {t('notification-issue-button')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      {!!lifeCycleDialogOpen && (
        <LifeCycleDialog
          dialogMode={lifeCycleDialogMode}
          open={lifeCycleDialogOpen}
          toggleStatus={toggleDialogOpen}
          {...(lifeCycleDialogMode !== 'new' &&
            dialogEvent !== null && { event: dialogEvent })}
        />
      )}
    </Grid>
  );
};

export default Notifications;
